import { useEffect, useState } from "react";
import Button from "../components/atoms/Button";
import IconButton from "../components/atoms/IconButton";
import Input from "../components/atoms/Input";
import Spinner from "../components/atoms/Spinner";
// import { WellnessProgramModal, ProgramAssignmentModal } from "../components/organisms";
import { useProfile } from "../hooks/auth";
import { WellnessService } from "../services/wellness";
import type {
  WellnessProgram,
  // CreateWellnessProgramRequest,
  // UpdateWellnessProgramRequest,
  WellnessFilters,
  ProgramStatistics,
} from "../types/wellness";

export function WellnessProgramsPage() {
  const { data: user } = useProfile();
  const [programs, setPrograms] = useState<WellnessProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<WellnessFilters>({
    search: "",
    isActive: true,
  });
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<WellnessProgram | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<WellnessProgram | null>(null);
  const [programStats, setProgramStats] = useState<Record<number, ProgramStatistics>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    programId: number;
    programName: string;
  }>({ show: false, programId: 0, programName: "" });

  const isAdmin = user?.realmRole === "admin" || user?.realmRole === "treel-admin";

  useEffect(() => {
    void loadPrograms();
  }, [filters]);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await WellnessService.getPrograms(filters);
      setPrograms(data);
      
      // Load statistics for admin users
      if (isAdmin) {
        await loadProgramStatistics(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load wellness programs");
    } finally {
      setLoading(false);
    }
  };

  const loadProgramStatistics = async (programs: WellnessProgram[]) => {
    try {
      const statsPromises = programs.map((program) =>
        WellnessService.getProgramStatistics(program.id)
          .then((stats) => ({ id: program.id, stats }))
          .catch(() => ({ id: program.id, stats: null }))
      );
      
      const results = await Promise.all(statsPromises);
      const statsMap: Record<number, ProgramStatistics> = {};
      results.forEach((result) => {
        if (result.stats) {
          statsMap[result.id] = result.stats;
        }
      });
      setProgramStats(statsMap);
    } catch (error) {
      console.error("Failed to load program statistics:", error);
    }
  };

  // Will be used when the form implementation is complete
  // const handleSaveProgram = async (data: CreateWellnessProgramRequest | UpdateWellnessProgramRequest) => {
  //   try {
  //     setError(null);

  //     if (editingProgram) {
  //       const updated = await WellnessService.updateProgram(editingProgram.id, data as UpdateWellnessProgramRequest);
  //       setPrograms((prev) =>
  //         prev.map((p) => (p.id === editingProgram.id ? updated : p))
  //       );
  //     } else {
  //       const newProgram = await WellnessService.createProgram(data as CreateWellnessProgramRequest);
  //       setPrograms((prev) => [...prev, newProgram]);
  //     }

  //     setShowProgramModal(false);
  //     setEditingProgram(null);
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : "Failed to save wellness program");
  //   }
  // };

  const handleDeleteProgram = async () => {
    if (!deleteConfirm.programId) return;

    try {
      setError(null);
      await WellnessService.deleteProgram(deleteConfirm.programId);
      setPrograms((prev) => prev.filter((p) => p.id !== deleteConfirm.programId));
      setDeleteConfirm({ show: false, programId: 0, programName: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete wellness program");
    }
  };

  const handleAssignProgram = (program: WellnessProgram) => {
    setSelectedProgram(program);
    setShowAssignModal(true);
  };

  const filteredPrograms = programs.filter((program) => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        program.name.toLowerCase().includes(searchLower) ||
        program.description.toLowerCase().includes(searchLower) ||
        program.category.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Wellness Programs</h1>
        <p className="text-gray-600">
          Manage and organize wellness programs for your users
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search programs..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="w-full"
          />
        </div>
        
        <div className="flex gap-2">
          <Button
            variant={filters.isActive === true ? "primary" : "secondary"}
            onClick={() => setFilters({ ...filters, isActive: true })}
          >
            Active
          </Button>
          <Button
            variant={filters.isActive === false ? "primary" : "secondary"}
            onClick={() => setFilters({ ...filters, isActive: false })}
          >
            Inactive
          </Button>
          <Button
            variant={filters.isActive === undefined ? "primary" : "secondary"}
            onClick={() => setFilters({ ...filters, isActive: undefined })}
          >
            All
          </Button>
        </div>

        <div className="flex gap-2">
          <IconButton
            icon={viewMode === "grid" ? "📋" : "📊"}
            aria-label="Toggle view mode"
            onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
          />
          
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => {
                setEditingProgram(null);
                setShowProgramModal(true);
              }}
            >
              + New Program
            </Button>
          )}
        </div>
      </div>

      {/* Programs Display */}
      {filteredPrograms.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No wellness programs found</p>
          {isAdmin && (
            <Button
              variant="primary"
              onClick={() => {
                setEditingProgram(null);
                setShowProgramModal(true);
              }}
            >
              Create First Program
            </Button>
          )}
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPrograms.map((program) => (
            <div
              key={program.id}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {program.name}
                </h3>
                <span className="inline-block px-3 py-1 text-sm rounded-full bg-blue-100 text-blue-800">
                  {program.category}
                </span>
              </div>
              
              <p className="text-gray-600 mb-4 line-clamp-3">
                {program.description}
              </p>
              
              <div className="mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Duration:</span>
                  <span className="font-medium">
                    {program.duration} {program.durationUnit}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Components:</span>
                  <span className="font-medium">
                    {program.goals.length} goals, {program.tasks.length} tasks
                  </span>
                </div>
                {isAdmin && programStats[program.id] && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Active Users:</span>
                    <span className="font-medium">
                      {programStats[program.id].activeUsers}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                {isAdmin && (
                  <>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        setEditingProgram(program);
                        setShowProgramModal(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleAssignProgram(program)}
                    >
                      Assign
                    </Button>
                    <IconButton
                      icon="🗑️"
                      variant="ghost"
                      size="small"
                      aria-label="Delete program"
                      onClick={() =>
                        setDeleteConfirm({
                          show: true,
                          programId: program.id,
                          programName: program.name,
                        })
                      }
                    />
                  </>
                )}
                <Button
                  variant="primary"
                  size="small"
                  onClick={() => {
                    // View program details
                    setSelectedProgram(program);
                  }}
                >
                  View Details
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Program
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Components
                </th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Active Users
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPrograms.map((program) => (
                <tr key={program.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {program.name}
                      </div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {program.description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {program.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {program.duration} {program.durationUnit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {program.goals.length} goals, {program.tasks.length} tasks
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {programStats[program.id]?.activeUsers || 0}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        program.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {program.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      {isAdmin && (
                        <>
                          <button
                            className="text-indigo-600 hover:text-indigo-900"
                            onClick={() => {
                              setEditingProgram(program);
                              setShowProgramModal(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            onClick={() => handleAssignProgram(program)}
                          >
                            Assign
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            onClick={() =>
                              setDeleteConfirm({
                                show: true,
                                programId: program.id,
                                programName: program.name,
                              })
                            }
                          >
                            Delete
                          </button>
                        </>
                      )}
                      <button
                        className="text-blue-600 hover:text-blue-900"
                        onClick={() => setSelectedProgram(program)}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md">
            <h3 className="text-lg font-semibold mb-4">Delete Wellness Program</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{deleteConfirm.programName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setDeleteConfirm({ show: false, programId: 0, programName: "" })}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={handleDeleteProgram}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals - These components need to be created */}
      {showProgramModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingProgram ? "Edit Wellness Program" : "Create New Wellness Program"}
            </h2>
            {/* Program form would go here */}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowProgramModal(false);
                  setEditingProgram(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={() => {
                // For now, just close the modal
                // In a real implementation, you'd collect form data and call handleSaveProgram
                setShowProgramModal(false);
                setEditingProgram(null);
              }}>
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && selectedProgram && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-4">Assign Program</h2>
            <p className="text-gray-600 mb-4">
              Assign "{selectedProgram.name}" to a user
            </p>
            {/* Assignment form would go here */}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedProgram(null);
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={() => {
                // For now, just close the modal
                // In a real implementation, you'd collect user ID and call WellnessService.assignProgram
                setShowAssignModal(false);
                setSelectedProgram(null);
              }}>
                Assign
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}